import { memo } from "react";
import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import type { SymbolDefinition, TerminalDef } from "@electro-planner/symbols";
import { SYMBOL_SIZE } from "../constants.ts";

export type ElectricalSymbolNodeData = {
  symbolId: string;
  designation: string;
  rotation: 0 | 90 | 180 | 270;
  // Symbol-definitionen skickas in som data för att undvika repeat-lookup vid varje render.
  // Projection-hooken injicerar denna när noder byggs från domänobjekten.
  symbol: SymbolDefinition;
};

export type ElectricalSymbolNodeType = Node<
  ElectricalSymbolNodeData,
  "electricalSymbol"
>;

const POSITION_MAP: Record<TerminalDef["position"], Position> = {
  top: Position.Top,
  bottom: Position.Bottom,
  left: Position.Left,
  right: Position.Right,
};

function terminalStyle(def: TerminalDef): React.CSSProperties {
  const pct = `${def.offset * 100}%`;
  switch (def.position) {
    case "top":
      return { top: 0, left: pct };
    case "bottom":
      return { bottom: 0, top: "auto", left: pct };
    case "left":
      return { left: 0, top: pct };
    case "right":
      return { right: 0, left: "auto", top: pct };
  }
}

function terminalColor(
  kind: "phase" | "neutral" | "earth" | "control" | "signal",
): string {
  switch (kind) {
    case "earth":
      return "#16a34a";
    case "neutral":
      return "#2563eb";
    case "control":
      return "#f59e0b";
    case "signal":
      return "#a855f7";
    case "phase":
      return "#111827";
  }
}

function ElectricalSymbolNodeImpl(props: NodeProps<ElectricalSymbolNodeType>) {
  const { data, selected } = props;
  const { symbol, designation, rotation } = data;

  return (
    <div
      className="relative"
      style={{
        width: SYMBOL_SIZE,
        height: SYMBOL_SIZE,
        outline: selected ? "2px solid var(--color-primary, #0f766e)" : "none",
        outlineOffset: 2,
        background: "white",
      }}
    >
      <svg
        width={SYMBOL_SIZE}
        height={SYMBOL_SIZE}
        viewBox={symbol.viewBox}
        style={{
          display: "block",
          transform: rotation ? `rotate(${rotation}deg)` : undefined,
          transformOrigin: "center",
          color: "var(--color-fg, #111827)",
        }}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: inline SVG från kontrollerat library
        dangerouslySetInnerHTML={{ __html: symbol.svg }}
      />
      <div
        className="pointer-events-none absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-gray-700"
        style={{ whiteSpace: "nowrap" }}
      >
        {designation}
      </div>
      {symbol.terminals.map((t) => (
        <Handle
          key={t.id}
          id={t.id}
          type="source"
          position={POSITION_MAP[t.position]}
          style={{
            width: 8,
            height: 8,
            background: terminalColor(t.kind),
            border: "1px solid white",
            ...terminalStyle(t),
          }}
          isConnectableStart={true}
          isConnectableEnd={true}
        />
      ))}
    </div>
  );
}

/**
 * Explicit equality — default memo jämför data-objektet via referens
 * och React Flow skapar nytt data-objekt vid varje projektion.
 */
export const ElectricalSymbolNode = memo(
  ElectricalSymbolNodeImpl,
  (prev, next) =>
    prev.id === next.id &&
    prev.selected === next.selected &&
    prev.data.symbolId === next.data.symbolId &&
    prev.data.designation === next.data.designation &&
    prev.data.rotation === next.data.rotation &&
    prev.data.symbol === next.data.symbol,
);
ElectricalSymbolNode.displayName = "ElectricalSymbolNode";
